import { useEffect, useState } from 'react'
import { PALETTE_SLOTS, type BoardTheme, type OrgDto } from '@praesto/shared'
import { api } from '../api.js'
import { ColorPicker } from './ColorPicker.js'
import { Dialog } from './Dialog.js'
import { useText } from '../i18n.js'
import { FontLibrary, FontSelect, useOrgFonts } from './FontLibrary.js'

/**
 * Board-Design.
 *
 * Sechs Hausfarben und zwei Schriften — mehr braucht eine Präsentation nicht,
 * und weniger führt dazu, dass jede Kleinigkeit einzeln entschieden wird. Die
 * Palette erscheint anschließend in jedem Farbwähler an erster Stelle.
 */
export function ThemeDialog({
  theme,
  background,
  orgId,
  onChange,
  onChangeBackground,
  onClose,
}: {
  theme: BoardTheme
  background: string
  /** Organisation des Boards — sie hält den Schriftenbestand und den Standard. */
  orgId: string
  onChange: (theme: BoardTheme) => void
  onChangeBackground: (color: string) => void
  onClose: () => void
}): React.ReactElement {
  const t = useText()
  const [fonts, setFonts] = useOrgFonts(orgId)
  const [org, setOrg] = useState<OrgDto | null>(null)

  // Die eigene Organisation wird hier nachgeschlagen und nicht durchgereicht:
  // Die Board-Ansicht braucht sie sonst nirgends, und ein Board-Link führt auch
  // in eine Organisation, die gerade nicht die gewählte ist.
  useEffect(() => {
    api
      .orgs()
      .then((list) => setOrg(list.find((entry) => entry.id === orgId) ?? null))
      .catch(() => setOrg(null))
  }, [orgId])

  const slots = Array.from(
    { length: PALETTE_SLOTS },
    (_, index) => theme.palette[index] ?? '#888888',
  )

  const setSlot = (index: number, color: string): void => {
    const palette = [...slots]
    palette[index] = color
    onChange({ ...theme, palette })
  }

  return (
    <Dialog title={t('theme.title')} onClose={onClose}>
      <p className="muted small">{t('theme.lead')}</p>

      <label className="field">
        <span>{t('theme.background')}</span>
        <span className="theme-row">
          <ColorPicker value={background} onChange={onChangeBackground} palette={theme.palette} />
          <span className="muted small">{background}</span>
        </span>
      </label>

      <label className="field">
        <span>{t('theme.palette')}</span>
        <span className="theme-row">
          {slots.map((color, index) => (
            <ColorPicker
              key={index}
              value={color}
              onChange={(next) => setSlot(index, next)}
              title={t('theme.color', { number: index + 1 })}
            />
          ))}
        </span>
      </label>

      <FontSelect
        label={t('theme.headings')}
        value={theme.headingFont}
        fonts={fonts}
        showPreview
        onChange={(headingFont) => onChange({ ...theme, headingFont })}
      />
      <FontSelect
        label={t('theme.body')}
        value={theme.bodyFont}
        fonts={fonts}
        showPreview
        onChange={(bodyFont) => onChange({ ...theme, bodyFont })}
      />

      {org && (
        <button
          className="button ghost"
          onClick={() => {
            onChangeBackground(org.design.background)
            onChange(org.design.theme)
          }}
          title={t('theme.resetTitle')}
        >
          {t('theme.reset', { org: org.name })}
        </button>
      )}

      <FontLibrary
        orgId={orgId}
        fonts={fonts}
        onChange={setFonts}
        canRemove={org?.role === 'admin'}
      />
    </Dialog>
  )
}
